import { create } from 'zustand'
import type { AuthTokens } from './types'

/**
 * Where the tokens live, and why.
 *
 * **Access token: memory only.** It is short-lived (15 minutes) and never
 * persisted, so a stolen storage dump does not hand over a working credential.
 *
 * **Refresh token: localStorage.** This is a deliberate, accepted exposure, and
 * it is worth being explicit about rather than discovering later:
 *
 *  - The API is bearer-only. It issues no cookie, so `httpOnly` — the storage
 *    that JavaScript cannot read — is not on the menu. There is nothing to
 *    choose between here; the alternative is not "a cookie", it is "nothing".
 *  - Keeping it in memory instead would mean logging in again on every page
 *    reload, which for a phone at a table is worse than the risk it avoids.
 *  - So the mitigation is upstream: a strict Content-Security-Policy, and never
 *    rendering untrusted HTML. There is no `dangerouslySetInnerHTML` anywhere in
 *    this client, and adding one is the thing that would make this decision wrong.
 *
 * The blast radius is bounded: `POST /auth/logout` rotates the Identity security
 * stamp, which invalidates every outstanding refresh token for the account at
 * once. That is the revocation lever if a token is ever believed stolen.
 */
const REFRESH_TOKEN_KEY = 'harrowgate.refreshToken'

/**
 * Storage access is wrapped because it throws rather than degrades in a few real
 * cases — Safari private browsing, and any browser with site data disabled. A
 * client that cannot persist a refresh token should still run; it just asks for
 * a password more often.
 */
function readStoredRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
  } catch {
    return null
  }
}

function writeStoredRefreshToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(REFRESH_TOKEN_KEY)
    else localStorage.setItem(REFRESH_TOKEN_KEY, token)
  } catch {
    // Non-fatal: the session still works for as long as this tab lives.
  }
}

interface TokenState {
  accessToken: string | null
  refreshToken: string | null
  setTokens: (tokens: AuthTokens) => void
  clear: () => void
}

export const useTokenStore = create<TokenState>()((set) => ({
  accessToken: null,
  refreshToken: readStoredRefreshToken(),

  setTokens: (tokens) => {
    writeStoredRefreshToken(tokens.refreshToken)
    set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken })
  },

  clear: () => {
    writeStoredRefreshToken(null)
    set({ accessToken: null, refreshToken: null })
  },
}))

// Non-hook accessors. The API client is plain functions, not React, and reads
// these synchronously on every request.
export const getAccessToken = (): string | null => useTokenStore.getState().accessToken
export const getRefreshToken = (): string | null => useTokenStore.getState().refreshToken
export const setTokens = (tokens: AuthTokens): void => useTokenStore.getState().setTokens(tokens)
export const clearTokens = (): void => useTokenStore.getState().clear()
